import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  LuArrowRight,
  LuClock3,
  LuInfo,
  LuShieldCheck,
  LuUser,
  LuUserCog,
  LuUserPlus,
  LuUsers,
} from "react-icons/lu";
import { fetchLogisticsTeamSummary } from "../../services/logisticsRfqUserAssignment.service";
import "../../assets/styles/logistics_team_management_notice.scss";
import "../../assets/styles/logistics_rfq_super_admin_notice.scss";

const FEATURE_ITEMS = [
  {
    icon: LuUserPlus,
    title: "Create Logistics Users",
    description:
      "To streamline RFQ processing, create Logistics User accounts and assign them the appropriate Logistics User role.",
  },
  {
    icon: LuUsers,
    title: "Team Responsibilities",
    description:
      "Logistics Users work under the Logistics Manager and can handle assigned Business Relocation and Exhibition Logistics RFQs.",
  },
  {
    icon: LuClock3,
    title: "5-Minute Assignment Window",
    description:
      "If one or more active Logistics Users are available, newly received RFQs will remain with the Logistics Manager for 5 minutes. If they are not manually assigned within that time, the system will automatically assign them to Logistics Users using a Round Robin algorithm.",
  },
  {
    icon: LuUser,
    title: "Manager Fallback",
    description:
      "If no active Logistics Users are available, the RFQ will remain assigned to the Logistics Manager, who is responsible for handling it directly.",
  },
  {
    icon: LuUserCog,
    title: "Manual Assignment",
    description:
      "Logistics Managers can manually assign RFQs to Logistics Users at any time before the automatic assignment occurs.",
  },
  {
    icon: LuShieldCheck,
    title: "Provider Assignment",
    description:
      "Only the current RFQ assignee (Logistics Manager or the assigned Logistics User) can assign Logistics Providers for that RFQ.",
  },
];

export default function LogisticsTeamManagementNotice({
  show = false,
  basePath = "/dashboard/logistics-manager",
}) {
  const [logisticsUserCount, setLogisticsUserCount] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  useEffect(() => {
    if (!show) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const summary = await fetchLogisticsTeamSummary();
        if (!cancelled) {
          setLogisticsUserCount(Math.trunc(Number(summary.logisticsUserCount) || 0));
        }
      } catch {
        if (!cancelled) setLogisticsUserCount(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [show]);

  if (!show) return null;

  const userCountLabel =
    logisticsUserCount == null
      ? "Logistics Users"
      : `${logisticsUserCount} Logistics User${logisticsUserCount === 1 ? "" : "s"}`;

  return (
    <section className="lm-team-notice" role="note" aria-labelledby="lm-team-notice-title">
      <div className="lm-team-notice__banner">
        <div className="lm-team-notice__content">
          <span className="lm-team-notice__icon" aria-hidden>
            <LuInfo />
          </span>
          <div className="lm-team-notice__copy">
            <h2 id="lm-team-notice-title">Round Robin is enabled.</h2>
            <p>
              <strong>{userCountLabel}</strong> available. New RFQs will be automatically assigned
              to users after 5 minutes.
              <br />
              If no Logistics Users are available, RFQs will remain assigned to you.
            </p>
          </div>
        </div>

        <div className="lm-team-notice__divider" aria-hidden />

        <div className="lm-team-notice__actions">
          <Link
            to={`${basePath}/users/create?roleLevel=logistics_user`}
            className="lm-team-notice__cta sa-access-btn sa-access-btn--primary"
          >
            <LuUsers aria-hidden />
            Create Logistics User
          </Link>
          <button
            type="button"
            className="lm-team-notice__learn-more"
            onClick={() => setDetailsOpen((open) => !open)}
            aria-expanded={detailsOpen}
            aria-controls="lm-team-notice-details"
          >
            {detailsOpen ? "Show less" : "Learn more"}
            <LuArrowRight aria-hidden />
          </button>
        </div>
      </div>

      {detailsOpen ? (
        <div
          id="lm-team-notice-details"
          className="lm-team-notice__panel sa-logistics-rfq-notice sa-logistics-rfq-notice--panel sa-logistics-rfq-notice--panel-expanded"
        >
          <div className="sa-logistics-rfq-notice__panel-head">
            <div className="sa-logistics-rfq-notice__panel-head-main">
              <span className="sa-logistics-rfq-notice__panel-icon" aria-hidden>
                <LuInfo />
              </span>
              <h2>Logistics Team Management Notice</h2>
            </div>
          </div>

          <div className="sa-logistics-rfq-notice__panel-grid lm-team-notice__panel-grid">
            {FEATURE_ITEMS.map((item, index) => {
              const Icon = item.icon;
              return (
                <article
                  key={item.title}
                  className={`sa-logistics-rfq-notice__panel-item${
                    index < FEATURE_ITEMS.length - 1
                      ? " sa-logistics-rfq-notice__panel-item--divider"
                      : ""
                  }`}
                >
                  <span className="sa-logistics-rfq-notice__panel-item-icon" aria-hidden>
                    <Icon />
                  </span>
                  <h3>{item.title}</h3>
                  <p>{item.description}</p>
                </article>
              );
            })}
          </div>
        </div>
      ) : null}
    </section>
  );
}
